import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMagic, faSpinner, faTimes } from '@fortawesome/free-solid-svg-icons'
import { ensureAssistFeature, requestFieldAssist } from '../../../store/engagement/assist'
import './ZyniFieldAssist.scss'

type Operation = 'generate' | 'revise'

interface Candidate {
    text: string
    format: 'plain' | 'html'
    rationale?: string
}

interface QuickAction {
    key: string
    label: string
    operation: Operation
    instruction: (brief: string) => string
    needsValue: boolean
}

const QUICK_ACTIONS: QuickAction[] = [
    {
        key: 'draft',
        label: 'Draft',
        operation: 'generate',
        instruction: (brief) => brief,
        needsValue: false,
    },
    {
        key: 'improve',
        label: 'Improve',
        operation: 'revise',
        instruction: (brief) => brief || 'Improve the clarity and flow while keeping the meaning.',
        needsValue: true,
    },
    {
        key: 'shorten',
        label: 'Shorten',
        operation: 'revise',
        instruction: (brief) => ['Make it shorter and tighter.', brief].filter(Boolean).join(' '),
        needsValue: true,
    },
    {
        key: 'expand',
        label: 'Expand',
        operation: 'revise',
        instruction: (brief) => ['Add more concrete detail.', brief].filter(Boolean).join(' '),
        needsValue: true,
    },
]

const HISTORY_LIMIT = 8

// A CSS `transform` on an ancestor (e.g. react-draggable's inline transform on a
// draggable modal dialog, applied even at rest) redefines the containing block for
// `position: fixed` descendants - "fixed" then resolves against that ancestor's box,
// not the real viewport. Walk up to find such an ancestor and return its viewport
// offset so fixed coordinates can be translated into its local frame; {0, 0} (the
// viewport itself) when there isn't one.
const findContainingBlockOffset = (node: HTMLElement | null): { left: number; top: number } => {
    let el = node?.parentElement ?? null
    while (el && el !== document.body) {
        const style = window.getComputedStyle(el)
        const establishesContainingBlock =
            (style.transform && style.transform !== 'none') ||
            (style.willChange && style.willChange.includes('transform')) ||
            (style.filter && style.filter !== 'none') ||
            (style.perspective && style.perspective !== 'none')
        if (establishesContainingBlock) {
            const rect = el.getBoundingClientRect()
            return { left: rect.left, top: rect.top }
        }
        el = el.parentElement
    }
    return { left: 0, top: 0 }
}

const stripToPlain = (value: string): string =>
    String(value ?? '')
        .replace(/<br\s*\/?>(?=)/gi, '\n')
        .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/\s*\n\s*/g, '\n')
        .replace(/[ \t]+/g, ' ')
        .trim()

interface Props {
    surface: string
    fieldKey: string
    variant?: string
    recordRef: Record<string, any>
    value: string
    format?: 'plain' | 'html'
    label?: string
    disabled?: boolean
    onApply: (text: string) => void
    onAppend?: (text: string) => void
}

export const ZyniFieldAssist: React.FC<Props> = ({
    surface,
    fieldKey,
    variant,
    recordRef,
    value,
    format = 'html',
    label,
    disabled = false,
    onApply,
    onAppend,
}) => {
    const dispatch = useDispatch()
    const feature = useSelector((state: any) => state.engagement_assist?.feature)

    const [open, setOpen] = useState(false)
    const [brief, setBrief] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [candidate, setCandidate] = useState<Candidate | null>(null)
    const [notes, setNotes] = useState('')
    const [applied, setApplied] = useState(false)
    const [copied, setCopied] = useState(false)

    const interactionIdRef = useRef<number | null>(null)
    const historyRef = useRef<Array<{ role: 'user' | 'assistant'; content: string }>>([])
    const lastRunRef = useRef<{ operation: Operation; instruction: string } | null>(null)
    const isMountedRef = useRef(true)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    const [panelPosition, setPanelPosition] = useState<{ top: number; left: number; maxHeight: number } | null>(null)

    useEffect(() => {
        isMountedRef.current = true
        dispatch(ensureAssistFeature() as any)
        return () => {
            isMountedRef.current = false
        }
    }, [dispatch])

    // Keep the panel inside the viewport regardless of where the trigger sits
    // (it can be near either edge of a Bootstrap modal). Recompute on
    // open/resize/scroll since the trigger's position can shift under any of those.
    // useLayoutEffect (not useEffect) so the first position is applied before
    // paint - no visible flash at the old corner.
    useLayoutEffect(() => {
        if (!open) {
            setPanelPosition(null)
            return
        }

        const PANEL_WIDTH = 340
        const ESTIMATED_PANEL_HEIGHT = 420
        const VIEWPORT_MARGIN = 8

        const computePosition = () => {
            const trigger = triggerRef.current
            if (!trigger) return

            const rect = trigger.getBoundingClientRect()
            const containingBlock = findContainingBlockOffset(trigger)

            const maxLeft = Math.max(VIEWPORT_MARGIN, window.innerWidth - PANEL_WIDTH - VIEWPORT_MARGIN)
            const leftInViewport = Math.min(Math.max(rect.left, VIEWPORT_MARGIN), maxLeft)

            let topInViewport = rect.bottom + 6
            const fitsBelow = topInViewport + ESTIMATED_PANEL_HEIGHT <= window.innerHeight - VIEWPORT_MARGIN
            if (!fitsBelow && rect.top - ESTIMATED_PANEL_HEIGHT - 6 >= VIEWPORT_MARGIN) {
                topInViewport = rect.top - ESTIMATED_PANEL_HEIGHT - 6
            }

            // ESTIMATED_PANEL_HEIGHT only picks which side to open on - the actual
            // response (rationale + notes + candidate + actions) can end up taller
            // than that estimate. Cap to the real remaining space and let the panel
            // scroll internally (see .zyni-field-assist__panel's overflow-y) rather
            // than silently overflow past the viewport, which made Apply/Post
            // unreliable once a longer answer came back.
            const maxHeight = Math.max(120, window.innerHeight - topInViewport - VIEWPORT_MARGIN)

            setPanelPosition({
                top: topInViewport - containingBlock.top,
                left: leftInViewport - containingBlock.left,
                maxHeight,
            })
        }

        computePosition()
        window.addEventListener('resize', computePosition)
        window.addEventListener('scroll', computePosition, true)
        return () => {
            window.removeEventListener('resize', computePosition)
            window.removeEventListener('scroll', computePosition, true)
        }
    }, [open])

    // Close on an outside click so the panel (fixed-position, can visually sit
    // over other controls such as a modal's own footer buttons) never silently
    // eats a click meant for something behind it.
    useEffect(() => {
        if (!open) return

        const handleOutsideClick = (event: MouseEvent) => {
            const target = event.target as Node
            if (panelRef.current?.contains(target) || triggerRef.current?.contains(target)) {
                return
            }
            setOpen(false)
        }

        document.addEventListener('mousedown', handleOutsideClick)
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick)
        }
    }, [open])

    const hasValue = String(value ?? '').trim() !== ''

    // Gate on this field's own surface, not just the generic capability flag -
    // a user may have content assist enabled with a different surface's rollout.
    if (!feature?.checked || !feature?.enabled || !(feature?.surfaces || []).includes(surface)) {
        return null
    }

    const run = async (operation: Operation, instruction: string) => {
        lastRunRef.current = { operation, instruction }
        setLoading(true)
        setError(null)
        setApplied(false)
        setCopied(false)

        try {
            const data: any = await dispatch(
                requestFieldAssist({
                    surface,
                    field_key: fieldKey,
                    variant,
                    operation,
                    record_ref: recordRef,
                    field_value: hasValue ? value : '',
                    instruction: instruction || undefined,
                    history: historyRef.current.length ? historyRef.current : undefined,
                    interaction_id: interactionIdRef.current,
                }) as any,
            )

            if (!isMountedRef.current) return

            const next: Candidate | null = data?.assist?.candidates?.[0] || null
            setCandidate(next && next.text ? next : null)
            setNotes(data?.assist?.notes || '')
            interactionIdRef.current = data?.interaction_id || interactionIdRef.current

            if (next?.text) {
                historyRef.current = [
                    ...historyRef.current,
                    { role: 'user' as const, content: instruction || '(draft this field)' },
                    { role: 'assistant' as const, content: stripToPlain(next.text) },
                ].slice(-HISTORY_LIMIT)
            }

            if (!next || !next.text) {
                setError(data?.assist?.notes || 'Zyni did not return a draft. Try rewording your request.')
            }
        } catch (err: any) {
            if (!isMountedRef.current) return
            const message =
                err?.response?.data?.error?.message ||
                err?.message ||
                'Zyni could not help right now. Please try again.'
            setError(message)
        } finally {
            if (isMountedRef.current) setLoading(false)
        }
    }

    const handleQuickAction = (action: QuickAction) => {
        run(action.operation, action.instruction(brief.trim()))
    }

    const handleAsk = () => {
        const trimmed = brief.trim()
        if (hasValue) {
            run('revise', trimmed || 'Improve the clarity and flow while keeping the meaning.')
        } else {
            run('generate', trimmed)
        }
    }

    const handleRegenerate = () => {
        const last = lastRunRef.current
        if (last) run(last.operation, last.instruction)
    }

    const handleApply = () => {
        if (!candidate) return
        onApply(candidate.text)
        setApplied(true)
    }

    const handleAppend = () => {
        if (!candidate || !onAppend) return
        onAppend(candidate.text)
        setApplied(true)
    }

    const handleCopy = async () => {
        if (!candidate) return
        try {
            await navigator.clipboard.writeText(
                candidate.format === 'html' ? stripToPlain(candidate.text) : candidate.text,
            )
            setCopied(true)
            setTimeout(() => isMountedRef.current && setCopied(false), 1500)
        } catch (err) {
            /* clipboard unavailable - no-op */
        }
    }

    const handleDiscard = () => {
        setCandidate(null)
        setNotes('')
        setError(null)
        setApplied(false)
    }

    const closePanel = () => {
        setOpen(false)
    }

    const availableActions = QUICK_ACTIONS.filter((action) => !action.needsValue || hasValue)

    return (
        <span className="zyni-field-assist">
            <button
                type="button"
                ref={triggerRef}
                className="zyni-field-assist__trigger"
                onClick={() => setOpen((prev) => !prev)}
                disabled={disabled}
                aria-expanded={open}
                aria-haspopup="dialog"
                title="Ask Zyni to help with this field"
            >
                <FontAwesomeIcon icon={faMagic} className="zyni-field-assist__trigger-icon" />
                <span>Zyni</span>
            </button>

            {open && (
                <div
                    ref={panelRef}
                    className="zyni-field-assist__panel"
                    style={panelPosition ? { top: panelPosition.top, left: panelPosition.left, maxHeight: panelPosition.maxHeight } : { visibility: 'hidden' }}
                    role="dialog"
                    aria-label={`Zyni assist${label ? ` for ${label}` : ''}`}
                >
                    <div className="zyni-field-assist__header">
                        <span className="zyni-field-assist__title">
                            Zyni{label ? <span className="zyni-field-assist__title-field"> · {label}</span> : null}
                        </span>
                        <button
                            type="button"
                            className="zyni-field-assist__close"
                            onClick={closePanel}
                            aria-label="Close Zyni assist"
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </button>
                    </div>

                    <div className="zyni-field-assist__actions">
                        {availableActions.map((action) => (
                            <button
                                key={action.key}
                                type="button"
                                className="zyni-field-assist__chip"
                                onClick={() => handleQuickAction(action)}
                                disabled={loading}
                            >
                                {action.label}
                            </button>
                        ))}
                    </div>

                    <textarea
                        className="zyni-field-assist__brief"
                        placeholder={
                            hasValue
                                ? 'Tell Zyni how to change this (optional)…'
                                : 'What do you want help with? (optional)'
                        }
                        value={brief}
                        onChange={(event) => setBrief(event.target.value)}
                        rows={2}
                        disabled={loading}
                    />

                    <button
                        type="button"
                        className="zyni-field-assist__ask"
                        onClick={handleAsk}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin /> Zyni is drafting…
                            </>
                        ) : hasValue ? (
                            'Ask Zyni to improve it'
                        ) : (
                            'Ask Zyni to draft it'
                        )}
                    </button>

                    {error && (
                        <div className="zyni-field-assist__error" role="alert">
                            {error}
                            <button type="button" className="zyni-field-assist__link" onClick={handleRegenerate}>
                                Try again
                            </button>
                        </div>
                    )}

                    {candidate && (
                        <div className="zyni-field-assist__candidate">
                            {candidate.format === 'html' ? (
                                <div
                                    className="zyni-field-assist__preview"
                                    // Server-sanitized to the editor's tag set (same trust model as datum rendering).
                                    dangerouslySetInnerHTML={{ __html: candidate.text }}
                                />
                            ) : (
                                <div className="zyni-field-assist__preview zyni-field-assist__preview--plain">
                                    {candidate.text}
                                </div>
                            )}

                            {candidate.rationale && (
                                <div className="zyni-field-assist__rationale">{candidate.rationale}</div>
                            )}
                            {notes && <div className="zyni-field-assist__notes">Note: {notes}</div>}

                            <div className="zyni-field-assist__candidate-actions">
                                <button
                                    type="button"
                                    className="zyni-field-assist__apply"
                                    onClick={handleApply}
                                    disabled={loading}
                                >
                                    {applied ? 'Applied ✓' : 'Apply'}
                                </button>
                                {onAppend && (
                                    <button
                                        type="button"
                                        className="zyni-field-assist__secondary"
                                        onClick={handleAppend}
                                        disabled={loading}
                                    >
                                        Append
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="zyni-field-assist__secondary"
                                    onClick={handleCopy}
                                    disabled={loading}
                                >
                                    {copied ? 'Copied' : 'Copy'}
                                </button>
                                <button
                                    type="button"
                                    className="zyni-field-assist__secondary zyni-field-assist__regenerate"
                                    onClick={handleRegenerate}
                                    disabled={loading}
                                >
                                    Regenerate
                                </button>
                                <button
                                    type="button"
                                    className="zyni-field-assist__link"
                                    onClick={handleDiscard}
                                    disabled={loading}
                                >
                                    Discard
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="zyni-field-assist__disclaimer">
                        Zyni drafts a suggestion — you decide whether it becomes part of your team's information.
                    </div>
                </div>
            )}
        </span>
    )
}

export default ZyniFieldAssist
